import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import {
  Server, Building2, Cable, Shield, Layers, Package, ArrowRight, Settings,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PropertyDashboard({ propertyId, token, onEditProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!propertyId || propertyId === 'overall') {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/reports/property-dashboard/${propertyId}`, { headers })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [propertyId, headers]);

  if (!propertyId || propertyId === 'overall') return null;

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, brand_compliance, integration_coverage, category_coverage, solutions } = data;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="property-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-2.5">
        <KpiCard
          icon={Package}
          label="Solutions"
          value={summary.solutions_deployed}
          sub={`of ${summary.total_platforms} platforms`}
          color="text-primary"
          testId="kpi-solutions"
        />
        <KpiCard
          icon={Building2}
          label="Vendors"
          value={summary.vendor_count}
          sub="unique vendors"
          color="text-indigo-600"
          testId="kpi-vendors"
        />
        <KpiCard
          icon={Shield}
          label="Brand Compliance"
          value={`${brand_compliance.compliance_pct}%`}
          sub={`${brand_compliance.brand_required + brand_compliance.brand_approved} brand / ${brand_compliance.non_brand} non-brand`}
          color={brand_compliance.compliance_pct >= 50 ? 'text-emerald-600' : 'text-amber-600'}
          testId="kpi-brand"
        />
        <KpiCard
          icon={Cable}
          label="Integration Coverage"
          value={`${integration_coverage.coverage_pct}%`}
          sub={`${integration_coverage.active} active / ${integration_coverage.gaps} gaps`}
          color={integration_coverage.coverage_pct >= 50 ? 'text-emerald-600' : 'text-red-500'}
          testId="kpi-integration"
        />
      </div>

      {/* Category Coverage Bar */}
      <div className="p-3 rounded-xl border bg-card/50">
        <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">Category Coverage</h4>
        <div className="flex gap-1.5 flex-wrap" data-testid="category-coverage-bars">
          {Object.entries(category_coverage).map(([catId, cat]) => {
            const pct = cat.total > 0 ? (cat.used / cat.total) * 100 : 0;
            return (
              <div key={catId} className="flex items-center gap-1.5" title={`${cat.name}: ${cat.used}/${cat.total}`}>
                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">{cat.name.split(' ')[0]}</span>
                <span className="text-[10px] font-mono font-semibold">{cat.used}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Integration Status */}
      {integration_coverage.total_template > 0 && (
        <div className="p-3 rounded-xl border bg-card/50">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">Integration Status</h4>
          <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-muted" data-testid="integration-bar">
            {integration_coverage.active > 0 && (
              <div className="bg-emerald-500 rounded-full" style={{ width: `${(integration_coverage.active / integration_coverage.total_template) * 100}%` }} title={`${integration_coverage.active} active`} />
            )}
            {integration_coverage.possible > 0 && (
              <div className="bg-blue-400 rounded-full" style={{ width: `${(integration_coverage.possible / integration_coverage.total_template) * 100}%` }} title={`${integration_coverage.possible} possible`} />
            )}
            {integration_coverage.gaps > 0 && (
              <div className="bg-red-300 rounded-full" style={{ width: `${(integration_coverage.gaps / integration_coverage.total_template) * 100}%` }} title={`${integration_coverage.gaps} gaps`} />
            )}
          </div>
          <div className="flex gap-4 mt-1.5 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Active ({integration_coverage.active})</span>
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-400" /> Possible ({integration_coverage.possible})</span>
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-300" /> Gaps ({integration_coverage.gaps})</span>
          </div>
        </div>
      )}

      {/* Solutions List */}
      <div className="p-3 rounded-xl border bg-card/50">
        <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
          Deployed Solutions ({solutions.length})
        </h4>
        <div className="space-y-1" data-testid="dashboard-solutions-list">
          {solutions.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px] p-1.5 rounded-lg bg-muted/30">
              <ArrowRight className="h-2.5 w-2.5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-medium leading-tight">{s.product_name || s.platform_name}</div>
                <div className="text-[9px] text-muted-foreground leading-tight">{s.platform_name}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="p-3 rounded-xl border bg-card/50 text-center" data-testid={testId}>
      <Icon className={`h-4 w-4 mx-auto mb-1 ${color}`} />
      <div className={`text-xl font-bold ${color}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{value}</div>
      <div className="text-[10px] font-semibold text-foreground">{label}</div>
      <div className="text-[9px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
